<template>
  <div>
    <div data-scroll-el="pdpContainer" />
    <div class="mx-a" :class="{ 'fit-finder-container': $feature.showFitFinderOnPDP }">
      <slot name="notifications" />
      <transition name="fade">
        <div v-if="productPending && !product">
          <slot name="skeletons" />
        </div>
        <div v-else-if="product">
          <div :class="brandClasses.grid">
            <slot name="breadcrumbs" />
            <section
              :class="brandClasses.gallery"
              :style="{ top: pxToRem(header.sticky.margin) }"
            >
              <template v-if="galleryPending">
                <div class="hidden lg:w-screen lg:flex lg:gap-2 lg:overflow-hidden">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="shrink-0 skeleton"
                    :style="{
                      'aspect-ratio': presets.src.width / presets.src.height,
                      'height': `max(44rem, calc(100dvh - ${pxToRem(header.height.full + 24)}))`,
                    }"
                  />
                </div>
                <div class="skeleton lg:hidden" :style="{ 'aspect-ratio': presets.src.width / presets.src.height }" />
              </template>
              <slot v-else name="gallery" />
            </section>

            <section :class="brandClasses.details">
              <slot name="heading" />
              <slot name="reviews-summary" />
              <slot name="pricing" />
              <slot name="espot-below-pricing" />
              <div class="mb-4" />
              <slot name="variant-selection" />
              <slot name="espot-below-sizes" />
              <slot name="gift-card" />
              <slot name="bundle-upsells" />
              <slot name="paypal-payments" />
              <div v-if="!isDigitalProduct" :class="brandClasses.ctasWrapper">
                <slot name="cta-add-to-cart" />
                <slot name="cta-apple-pay" />
                <slot name="cta-favorites" />
                <slot name="cta-reserve-in-store" />
                <slot name="cta-find-in-store" />
                <p v-if="showLegalDisclaimer && allowApplePayOrderOnPDP" class="mt-2 text-xs">
                  <form-legal-disclaimer
                    :content="$t.checkoutLegalText"
                    :data-test-ids="{
                      'sale-terms': 'pdp-terms-link',
                      'privacy': 'pdp-privacy-link',
                    }"
                  />
                </p>
                <slot name="klarna-payments" />
              </div>
              <slot name="upsells" />
              <slot name="shipping-methods" />
            </section>
          </div>

          <div class="pb-6 container space-y-6 lg:pb-8 lg:space-y-8">
            <slot name="espot-top-of-secondary-section" />
            <slot name="espot-above-accordions" />
            <slot name="accordions" />
            <slot name="espot-below-accordions" />
            <slot name="shop-the-look" />
            <section
              :class="[
                brandClasses.eSpotsAndReviews,
                $feature.showFullscreenGridOnPDP ? brandClasses.gridFullWidth : brandClasses.gridPartialWidth,
              ]"
            >
              <slot name="espot-above-reviews" />
              <slot name="reviews" />
            </section>
          </div>
        </div>
      </transition>
    </div>

    <div v-if="$feature.enablePDPESpots && eSpots?.['lazy-above-footer']" class="mt-8 container">
      <slot name="espot-lazy-above-footer" />
    </div>

    <slot name="sticky-cta-bar" />
    <slot />
  </div>
</template>

<script lang="ts" setup>
import type { Slots } from '#commerce/types/pdp'

defineSlots<Slots>()

const { brandClasses, gallery: { presets }, showLegalDisclaimer } = useAppConfig().pages.pdp
const header = useHeaderStore()
const { isDigitalProduct, eSpots, product, productPending } = usePdp()
const { galleryPending } = usePdp()
const { allowApplePayOrderOnPDP } = useExpressPaymentFeatures()
</script>
